import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import PublicLegalPageLayout, {
  Section,
  Text
} from '../../components/Common/PublicLegalPageLayout';
import GuideAccordionSection from '../../components/Support/GuideAccordionSection';
import { getGuideSectionIds } from '../../utils/supportGuideFaqConfig';
import { getRoleHomePath } from '../../utils/roleHomePath';

const resolveSectionContent = (t, sectionId) => {
  const base = `supportGuide.sections.${sectionId}`;
  const presentation = t(`${base}.presentation`, { defaultValue: '' })
    || t(`${base}.text`, { defaultValue: '' });
  const procedure = t(`${base}.procedure`, { returnObjects: true, defaultValue: null });
  if (Array.isArray(procedure) && procedure.length > 0) {
    return { presentation, procedure };
  }
  const steps = t(`${base}.steps`, { returnObjects: true, defaultValue: [] });
  return {
    presentation,
    procedure: Array.isArray(steps) ? steps : []
  };
};

const GuideUtilisateur = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'guest';
  const year = new Date().getFullYear();
  const sectionIds = getGuideSectionIds(role);
  const roleLabel = t(`supportGuide.roleLabels.${role}`, { defaultValue: role });

  const procedureTitle = t('supportGuide.procedureTitle');
  const expandLabel = t('supportGuide.expandSection');
  const collapseLabel = t('supportGuide.collapseSection');

  const sections = useMemo(
    () => sectionIds.map((sectionId) => ({
      id: sectionId,
      title: t(`supportGuide.sections.${sectionId}.title`),
      ...resolveSectionContent(t, sectionId)
    })),
    [sectionIds, t]
  );

  return (
    <PublicLegalPageLayout
      title={t('supportGuide.title')}
      backLabel={t('supportGuide.back')}
      onBack={() => navigate(user ? getRoleHomePath(role) : '/support')}
    >
      <Section>
        <Text>{t('supportGuide.intro')}</Text>
        <Text style={{ marginTop: '0.75rem', fontWeight: 600 }}>
          {t('supportGuide.roleBanner', { role: roleLabel })}
        </Text>
        <Text style={{ marginTop: '0.5rem', opacity: 0.9, fontSize: '0.95rem' }}>
          {t('supportGuide.accordionHint')}
        </Text>
      </Section>

      {sections.map((section, index) => (
        <GuideAccordionSection
          key={section.id}
          id={section.id}
          title={section.title}
          presentation={section.presentation}
          procedure={section.procedure}
          procedureTitle={procedureTitle}
          defaultOpen={index === 0}
          expandLabel={expandLabel}
          collapseLabel={collapseLabel}
        />
      ))}

      <Section>
        <Text style={{ textAlign: 'center', opacity: 0.8 }}>
          {t('supportPage.footer.appName')} — {t('supportPage.footer.rights', { year })}
        </Text>
      </Section>
    </PublicLegalPageLayout>
  );
};

export default GuideUtilisateur;
